import React, { useState, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import propTypes from 'prop-types';
import classNames from 'classnames';
import _filter from 'lodash/filter.js';
import _includes from 'lodash/includes.js';
import { Gallery } from '_ui-core-js/React/components/Gallery.jsx';
import { Overlay } from '_ui-core-js/React/components/Overlay.jsx';
import { useEventListener } from '_ui-core-js/React/hooks/useEventListener.js';
import { useUpdateEffect } from '_ui-core-js/React/hooks/useUpdateEffect.js';
import { publish } from '_ui-core-js/utilities/events.js';
import { CORE_GTM_TRACKING_VALUES } from '_ui-core-js/constants/analytics.js';
import { LBX_GTM_TRACKING_VALUES } from '../../constants/analytics.js';
import { fireGTMTrackingEvent } from '_ui-core-js/utilities/fireGTMTrackingEvent.js';

const VIEWS = { 
  gallery: 'gallery',
  inspiration: 'inspiration'
};

const SOCIAL_SHARE = {
  facebook: true,
  twitter: true,
  pinterest: true
};

const GLOBAL_HASHTAGS = [ '#ExperienceLBX','#ElevateTheEveryday'];

export function GalleryAndInspiration(props) {

  const { 
	gallery, 
	inspiration,
	atmosphere, 
	images, 
	largeImageViewer, 
	socialMediaShare,
	footer,
	overlay,
	user 
  } = props;

  const [activeView, setActiveView] = useState();
  const [largeImageViewerMode, setLargeImageViewerMode] = useState();
  const activeImageUrlRef = useRef();
  const activeImageIdRef = useRef();
  const block = 'c-gallery-and-inspiration';
  const {isUserLoggedIn, personalArtCollection } = user;
  const { buttonLabel } = overlay;
  const navigate = useNavigate();
  const galleryViewActive = activeView === VIEWS.gallery;
  const inspirationViewActive = activeView === VIEWS.inspiration;
  const trackingOverride = {
  	workflow: {
  		name: galleryViewActive ? LBX_GTM_TRACKING_VALUES.componentName.lbxYourGallery : LBX_GTM_TRACKING_VALUES.componentName.lbxInspiration,
  		step: CORE_GTM_TRACKING_VALUES.action.openOverlay,
  		conversion: 0,
  		variant: user.userPreferences && user.userPreferences.personality ? user.userPreferences.personality : CORE_GTM_TRACKING_VALUES.label.general
  	}
  }

  function removeImageId() {
	if (activeImageIdRef.current) {
	  navigate("/inspiration") 
	  activeImageIdRef.current = null
	}
  };

  const renderHashTags = (image, modifier) => {
	const marketHashTags = _.get(image, 'data.hashTags', []);
	const hashTags = marketHashTags.concat(GLOBAL_HASHTAGS);

	return (
		hashTags && 
	  <ul className={classNames({
		[`${block}__image-hashtags`]: true,
		[`${block}__image-hashtags--${modifier}`]: modifier
	  })}
	  >
		{
		hashTags.map(hashTag => (
		  <li className={`${block}__image-hashtag list-item`} key={hashTag}>
		  { hashTag }
		  </li>
		))
		}
	  </ul>
	  )
  };

  const largeImageViewerCaption = image => (
	<div className={`${block}__large-image-caption lbx-theme-${image.data.atmosphere.toLowerCase()}`}>
	<span className='h1'>LBX </span>
	<span className='h1 apply-lbx-theme-text'>
	  { image.data.atmosphere.toUpperCase() }
	</span>
	  { renderHashTags(image) }
	</div>
  );

  const gridImageOverlay = (image, modifier) => renderHashTags(image, modifier);

  function openOverlay(e) {
	const { overlayId, activeImageUrl, imageId } = e.detail
	setActiveView( isUserLoggedIn ? VIEWS.gallery: VIEWS.inspiration);
	activeImageUrlRef.current = activeImageUrl;
	activeImageIdRef.current = imageId;

	setTimeout(() => {
	  publish('trigger-open-overlay', { overlayId }, window);
	}, 0);
  };

  function resetView() {
	setActiveView(null);
	navigate("/");
  };

  useEventListener('trigger-open-gallery-and-inspiration-overlay', openOverlay);

  useEventListener('overlay-closed', resetView);

  useUpdateEffect(() => {
	  activeImageUrlRef.current = null;
  }, [activeView]);

  const galleryImages = _filter(images, { data : { atmosphere }})

  return (
	
	<Overlay 
	id='lbx-image-gallery'
	closeButtonLabel={buttonLabel}
	gtmComponentName={galleryViewActive ? 
	  LBX_GTM_TRACKING_VALUES.componentName.lbxYourGallery : 
	  LBX_GTM_TRACKING_VALUES.componentName.lbxInspiration}
	gtmComponentGroup={CORE_GTM_TRACKING_VALUES.componentGroup.newCars}
	gtmLabel={atmosphere}
	trackingOverride={trackingOverride}
	>
	  <div className={ classNames({
		[`${block} utils__section-padding--bottom container-xxxl`]: true,
		[`${block}--large-image-viewer-mode`]: largeImageViewerMode
	  })}>
		{
		  galleryViewActive && 
			<Gallery 
			header={gallery.header}
			images={[...personalArtCollection, ...galleryImages] }
			footer={footer}
			closeLargeImageViewerLabel={largeImageViewer.buttonLabel}
			largeImageViewerCaption={largeImageViewerCaption}
			viewChangeCallback={largeImageViewerActive => setLargeImageViewerMode(largeImageViewerActive)}
			gridImageOverlay={image => gridImageOverlay(image, 'overlay')}
			scrollableContainerSelector='.c-overlay'
			activeImageUrl={activeImageUrlRef.current}
			removeImageId={removeImageId}
			socialMediaShareLabel={socialMediaShare?.label || 'SHARE ONLINE'}
			socialMediaShareButtons={socialMediaShare?.buttons || SOCIAL_SHARE}
			key='gallery'
			gtmComponentName={LBX_GTM_TRACKING_VALUES.componentName.lbxYourGallery}
			gtmComponentGroup={CORE_GTM_TRACKING_VALUES.componentGroup.newCars}
			/>
		}
		{ 
		  inspirationViewActive &&
			<Gallery 
			header={inspiration.header}
			filters={inspiration.filters}
			images={images}
			footer={footer}
			closeLargeImageViewerLabel={largeImageViewer.buttonLabel}
			largeImageViewerCaption={largeImageViewerCaption}
			viewChangeCallback={largeImageViewerActive => setLargeImageViewerMode(largeImageViewerActive)}
			gridImageOverlay={image => gridImageOverlay(image, 'overlay')}
			scrollableContainerSelector='.c-overlay'
			activeImageUrl={activeImageUrlRef.current}
			removeImageId={removeImageId}
			socialMediaShareLabel={socialMediaShare?.label || 'SHARE ONLINE'}
			socialMediaShareButtons={socialMediaShare?.buttons || SOCIAL_SHARE}
			key='inspiration'
			gtmComponentName={LBX_GTM_TRACKING_VALUES.componentName.lbxInspiration}
			gtmComponentGroup={CORE_GTM_TRACKING_VALUES.componentGroup.newCars}
			/>
		}
		{
			isUserLoggedIn && 
			<div className={`${block}__toggle`}>
			  <button 
			  className={classNames({
			  [`${block}__toggle-button`]: true,
			  [`${block}__toggle-button--active`]: galleryViewActive
			  })} 
			  onClick={() => {
			  	setActiveView(VIEWS.gallery)
			  	fireGTMTrackingEvent({
			      name: CORE_GTM_TRACKING_VALUES.name.workflowEvent,
			      componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxYourGallery, 
			      componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
			      action: CORE_GTM_TRACKING_VALUES.action.openOverlay,
			      label: user.userPreferences && user.userPreferences.personality ? user.userPreferences.personality : CORE_GTM_TRACKING_VALUES.label.general
			    }, {
		  			workflow: {
				  		name: LBX_GTM_TRACKING_VALUES.componentName.lbxYourGallery,
				  		step: CORE_GTM_TRACKING_VALUES.action.openOverlay,
				  		conversion: 0,
				  		variant: user.userPreferences && user.userPreferences.personality ? user.userPreferences.personality : CORE_GTM_TRACKING_VALUES.label.general
			  		}
			  	})
			  }}
			  >
				{ gallery.buttonLabel }
			  </button>
			  <button className={classNames({
			  [`${block}__toggle-button`]: true,
			  [`${block}__toggle-button--active`]: inspirationViewActive
			  })}  
			  onClick={() => {
			  	setActiveView(VIEWS.inspiration) 
			  	fireGTMTrackingEvent({
			      name: CORE_GTM_TRACKING_VALUES.name.workflowEvent,
			      componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxInspiration, 
			      componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
			      action: CORE_GTM_TRACKING_VALUES.action.openOverlay,
			      label: user.userPreferences && user.userPreferences.personality ? user.userPreferences.personality : CORE_GTM_TRACKING_VALUES.label.general
			    }, {
		  			workflow: {
				  		name: LBX_GTM_TRACKING_VALUES.componentName.lbxInspiration,
				  		step: CORE_GTM_TRACKING_VALUES.action.openOverlay,
				  		conversion: 0,
				  		variant: user.userPreferences && user.userPreferences.personality ? user.userPreferences.personality : CORE_GTM_TRACKING_VALUES.label.general
			  		}
			  	})
			  }}
			  >
				{ inspiration.buttonLabel }
			  </button>
			</div>
		}
	  </div>
	</Overlay>
  )
};

GalleryAndInspiration.propTypes = {
  gallery: propTypes.object.isRequired,
  inspiration: propTypes.object,
  images: propTypes.array.isRequired,
  atmosphere: propTypes.string,
  largeImageViewer: propTypes.object.isRequired,
  socialMediaShare: propTypes.object,
  footer: propTypes.object.isRequired
};
